import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle2, Send, FileArchive } from 'lucide-react';
import { currentAcademicYear } from '@/lib/academicYear';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const RC_STATUS_LABELS = { DRAFT: 'Brouillon', GENERATED: 'Généré', VALIDATED: 'Validé', PUBLISHED: 'Publié', ARCHIVED: 'Archivé' };
const PERIODS = [['TRIMESTER_1', 'Trimestre 1'], ['TRIMESTER_2', 'Trimestre 2'], ['TRIMESTER_3', 'Trimestre 3'], ['SEMESTER_1', 'Semestre 1'], ['SEMESTER_2', 'Semestre 2'], ['ANNUAL', 'Annuel']];

// Circuit : Généré -> Validé (Directeur) -> Publié (Administrateur) ; ZIP par classe
export const ReportCardWorkflowBar = ({ user, classes, selectedIds, onDone, onScopeChange }) => {
  const [scope, setScope] = useState({ class_id: '', period: 'TRIMESTER_1', academic_year: currentAcademicYear() });
  const [busy, setBusy] = useState(false);
  const [hasPrincipal, setHasPrincipal] = useState(false);
  useEffect(() => {
    if (user.role !== 'ADMIN') return;
    axios.get(`${API}/users`, { params: { role: 'PRINCIPAL' } })
      .then((r) => setHasPrincipal(r.data.some((u) => u.role === 'PRINCIPAL' && u.status !== 'INACTIVE')))
      .catch(() => {});
  }, [user.role]);
  const canValidate = user.role === 'PRINCIPAL' || (user.role === 'ADMIN' && !hasPrincipal);
  const body = () => (selectedIds.length ? { report_ids: selectedIds } : scope);
  const ready = selectedIds.length > 0 || scope.class_id;

  const run = async (action) => {
    if (!ready) return toast.error('Sélectionnez des bulletins ou une classe');
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/report-cards/${action}`, body());
      toast.success(action === 'validate' ? `${data.validated_count} bulletin(s) validé(s)` : `${data.published_count} bulletin(s) publié(s)`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action impossible');
    } finally {
      setBusy(false);
    }
  };

  const zip = async () => {
    if (!scope.class_id) return toast.error('Choisissez une classe');
    setBusy(true);
    try {
      const res = await axios.get(`${API}/report-cards/download-zip`, { params: scope, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bulletins_${scope.academic_year}_${scope.period}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Aucun bulletin à télécharger pour cette sélection');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="card" data-testid="rc-workflow-bar">
      <CardContent className="pt-6 flex flex-wrap items-center gap-3">
        <Select value={scope.class_id} onValueChange={(v) => { setScope({ ...scope, class_id: v }); onScopeChange && onScopeChange(v); }}>
          <SelectTrigger className="w-56" data-testid="rc-scope-class"><SelectValue placeholder="Classe" /></SelectTrigger>
          <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={scope.period} onValueChange={(v) => setScope({ ...scope, period: v })}>
          <SelectTrigger className="w-40" data-testid="rc-scope-period"><SelectValue /></SelectTrigger>
          <SelectContent>{PERIODS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-sm text-gray-500">{selectedIds.length ? `${selectedIds.length} sélectionné(s)` : 'ou cochez des bulletins'}</span>
        <div className="flex gap-2 ml-auto">
          {canValidate && (
            <Button variant="outline" disabled={busy} onClick={() => run('validate')} data-testid="rc-validate-btn">
              <CheckCircle2 className="w-4 h-4 mr-2" />Valider
            </Button>
          )}
          {user.role === 'ADMIN' && (
            <Button disabled={busy} onClick={() => run('publish')} data-testid="rc-publish-btn"><Send className="w-4 h-4 mr-2" />Publier</Button>
          )}
          <Button variant="outline" disabled={busy} onClick={zip} data-testid="rc-zip-btn"><FileArchive className="w-4 h-4 mr-2" />ZIP</Button>
        </div>
      </CardContent>
    </Card>
  );
};
